/**
 * LandingNew — Main APISAI Landing v1 (Master Spec v1)
 *
 * Filozofia: APISAI = system widoczności i pozyskiwania klientów
 *   (strona + katalog + promocja + wiadomości).
 * Nie sprzedajemy "AI", "CMS", "marketplace". Sprzedajemy efekt.
 *
 * Sekcje (P0):
 *   1. PublicNav (z dropdownami Hodowle ▼ / Biznesy ▼)
 *   2. Hero ekosystem ("Więcej widoczności. Więcej klientów.")
 *   3. Visibility Ecosystem (Google, Bing, Mapy, FB, IG, WhatsApp, Katalog, AI Search)
 *   4. Katalog APISAI showcase
 *   5. Business Slider (Hodowle, Groomerzy, Restauracje, Bary, Hotele, Fryzjerzy)
 *   6. Wolfe Case Study (realne screeny)
 *   7. Jak działa APISAI (4 kroki)
 *   8. Live ekosystem ticker (real public events)
 *   9. Final CTA + Mobile sticky CTA
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, ArrowUp, ChevronDown, Menu as MenuIcon, X,
  Search, MessageSquare, Send, Eye, Sparkles, Scissors, UtensilsCrossed,
  Wine, Hotel, Briefcase, Dog, MapPin, Phone, CheckCircle2, ShieldCheck,
  Globe, MessageCircle, Megaphone, ArrowUpRight, PawPrint, GraduationCap, Award, Store,
} from 'lucide-react';
import PublicLiveTicker from '@/components/landing/PublicLiveTicker';

/* ─── STYLES ─── */
const STYLES = `
@keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
@keyframes pulseRing { 0% { box-shadow: 0 0 0 0 rgba(249,115,22,0.4); } 70% { box-shadow: 0 0 0 18px rgba(249,115,22,0); } 100% { box-shadow: 0 0 0 0 rgba(249,115,22,0); } }
@keyframes orbSlow { 0% { transform: translate(0,0); } 50% { transform: translate(15px, -25px); } 100% { transform: translate(0,0); } }
@keyframes ecoOrbit { from { transform: rotate(0deg) translateX(140px) rotate(0deg); } to { transform: rotate(360deg) translateX(140px) rotate(-360deg); } }
@keyframes grainShift { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-6px, 4px); } }
.anim-float { animation: float 5s ease-in-out infinite; }
.anim-float-delay { animation: float 5s ease-in-out 1.4s infinite; }
.anim-pulse-ring { animation: pulseRing 2.4s infinite; }
.anim-orb { animation: orbSlow 9s ease-in-out infinite; }
.grain-overlay { background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.08 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E"); }
.scrollbar-hide::-webkit-scrollbar { display: none; }
.scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
`;

/* ─── PUBLIC NAV ─── */
function PublicNav() {
  const [open, setOpen] = useState(false);
  const [openDD, setOpenDD] = useState(null); // 'hodowle' | 'biznesy' | null
  const ddRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ddRef.current && !ddRef.current.contains(e.target)) setOpenDD(null); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#070710]/80 border-b border-white/[0.07]" data-testid="public-nav">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <img src="/landing-spec/brand/apisai_logo_orange_hex.png" alt="APISAI" className="h-8 w-8 rounded-lg" />
          <span className="text-lg font-bold tracking-tight">APISAI</span>
        </Link>

        <div className="hidden lg:flex items-center gap-7 text-sm" ref={ddRef}>
          <Link to="/produkt" className="text-zinc-400 hover:text-white transition">Produkt</Link>

          {/* Dropdown Hodowle */}
          <div className="relative">
            <button
              onClick={() => setOpenDD(openDD === 'hodowle' ? null : 'hodowle')}
              className="flex items-center gap-1 text-zinc-400 hover:text-white transition"
              data-testid="nav-dropdown-hodowle"
            >
              Hodowle <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openDD === 'hodowle' ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {openDD === 'hodowle' && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
                  className="absolute top-9 left-0 w-56 bg-[#0c0c1a] border border-white/10 rounded-xl py-2 shadow-2xl shadow-black/40">
                  <Link to="/hodowle" className="block px-4 py-2 text-sm text-zinc-300 hover:bg-orange-500/10 hover:text-orange-300">Katalog hodowli</Link>
                  <Link to="/szczenieta" className="block px-4 py-2 text-sm text-zinc-300 hover:bg-orange-500/10 hover:text-orange-300">Szczenięta</Link>
                  <Link to="/reproduktory" className="block px-4 py-2 text-sm text-zinc-300 hover:bg-orange-500/10 hover:text-orange-300">Reproduktory</Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Dropdown Biznesy */}
          <div className="relative">
            <button
              onClick={() => setOpenDD(openDD === 'biznesy' ? null : 'biznesy')}
              className="flex items-center gap-1 text-zinc-400 hover:text-white transition"
              data-testid="nav-dropdown-biznesy"
            >
              Biznesy <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openDD === 'biznesy' ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {openDD === 'biznesy' && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
                  className="absolute top-9 left-0 w-60 bg-[#0c0c1a] border border-white/10 rounded-xl py-2 shadow-2xl shadow-black/40">
                  <Link to="/firmy" className="block px-4 py-2 text-sm text-zinc-300 hover:bg-orange-500/10 hover:text-orange-300">Katalog biznesów</Link>
                  <Link to="/firmy?branza=groomer" className="block px-4 py-2 text-sm text-zinc-300 hover:bg-orange-500/10 hover:text-orange-300">Groomerzy</Link>
                  <Link to="/firmy?branza=restauracja" className="block px-4 py-2 text-sm text-zinc-300 hover:bg-orange-500/10 hover:text-orange-300">Restauracje</Link>
                  <Link to="/firmy?branza=bar" className="block px-4 py-2 text-sm text-zinc-300 hover:bg-orange-500/10 hover:text-orange-300">Bary</Link>
                  <Link to="/firmy?branza=hotel" className="block px-4 py-2 text-sm text-zinc-300 hover:bg-orange-500/10 hover:text-orange-300">Hotele dla psów</Link>
                  <Link to="/firmy?branza=trener" className="block px-4 py-2 text-sm text-zinc-300 hover:bg-orange-500/10 hover:text-orange-300">Trenerzy</Link>
                  <Link to="/firmy?branza=fryzjer" className="block px-4 py-2 text-sm text-zinc-300 hover:bg-orange-500/10 hover:text-orange-300">Fryzjerzy</Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link to="/cennik" className="text-zinc-400 hover:text-white transition">Cennik</Link>
          <Link to="/blog" className="text-zinc-400 hover:text-white transition">Baza wiedzy</Link>
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/login" className="text-sm text-zinc-300 hover:text-white" data-testid="nav-login">Zaloguj się</Link>
          <Link to="/stworz"
            className="text-sm font-semibold px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 text-white transition shadow-lg shadow-orange-500/20"
            data-testid="nav-cta-register">
            Wygeneruj stronę za darmo
          </Link>
        </div>

        <button onClick={() => setOpen(true)} className="lg:hidden p-2 text-zinc-300" data-testid="nav-mobile-menu">
          <MenuIcon className="w-6 h-6" />
        </button>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.25 }}
            className="lg:hidden fixed inset-0 z-50 bg-[#070710] overflow-y-auto"
          >
            <div className="px-5 py-4 flex items-center justify-between border-b border-white/10">
              <span className="text-lg font-bold">APISAI</span>
              <button onClick={() => setOpen(false)} className="p-2" data-testid="nav-mobile-close"><X className="w-6 h-6" /></button>
            </div>
            <div className="px-5 py-6 space-y-1">
              <Link onClick={() => setOpen(false)} to="/produkt" className="block py-3 text-zinc-200 border-b border-white/5">Produkt</Link>
              <details className="border-b border-white/5">
                <summary className="py-3 cursor-pointer text-zinc-200 flex items-center justify-between">Hodowle <ChevronDown className="w-4 h-4" /></summary>
                <div className="pl-3 pb-3 space-y-2 text-sm text-zinc-400">
                  <Link onClick={() => setOpen(false)} to="/hodowle" className="block py-1">Katalog hodowli</Link>
                  <Link onClick={() => setOpen(false)} to="/szczenieta" className="block py-1">Szczenięta</Link>
                  <Link onClick={() => setOpen(false)} to="/reproduktory" className="block py-1">Reproduktory</Link>
                </div>
              </details>
              <details className="border-b border-white/5">
                <summary className="py-3 cursor-pointer text-zinc-200 flex items-center justify-between">Biznesy <ChevronDown className="w-4 h-4" /></summary>
                <div className="pl-3 pb-3 space-y-2 text-sm text-zinc-400">
                  <Link onClick={() => setOpen(false)} to="/firmy" className="block py-1">Katalog biznesów</Link>
                  <Link onClick={() => setOpen(false)} to="/firmy?branza=groomer" className="block py-1">Groomerzy</Link>
                  <Link onClick={() => setOpen(false)} to="/firmy?branza=restauracja" className="block py-1">Restauracje</Link>
                  <Link onClick={() => setOpen(false)} to="/firmy?branza=bar" className="block py-1">Bary</Link>
                  <Link onClick={() => setOpen(false)} to="/firmy?branza=hotel" className="block py-1">Hotele dla psów</Link>
                  <Link onClick={() => setOpen(false)} to="/firmy?branza=trener" className="block py-1">Trenerzy</Link>
                  <Link onClick={() => setOpen(false)} to="/firmy?branza=fryzjer" className="block py-1">Fryzjerzy</Link>
                </div>
              </details>
              <Link onClick={() => setOpen(false)} to="/cennik" className="block py-3 text-zinc-200 border-b border-white/5">Cennik</Link>
              <Link onClick={() => setOpen(false)} to="/blog" className="block py-3 text-zinc-200 border-b border-white/5">Baza wiedzy</Link>
              <div className="pt-6 space-y-3">
                <Link onClick={() => setOpen(false)} to="/login"
                  className="block w-full text-center py-3 rounded-xl border border-white/15 text-zinc-200">Zaloguj się</Link>
                <Link onClick={() => setOpen(false)} to="/stworz"
                  className="block w-full text-center py-3 rounded-xl bg-orange-500 text-white font-semibold">Wygeneruj stronę za darmo</Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

/* ─── HERO ECOSYSTEM VISUAL ─── */
function HeroEcosystemVisual() {
  return (
    <div className="relative w-full h-[420px] sm:h-[500px]" data-testid="hero-ecosystem-visual">
      {/* Orange/violet glow background */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full bg-orange-500/20 blur-3xl anim-orb" />
        <div className="absolute bottom-0 right-1/4 w-[320px] h-[320px] rounded-full bg-violet-600/25 blur-3xl anim-orb" style={{ animationDelay: '2s' }} />
      </div>

      {/* Card: Strona / profil */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        className="absolute top-3 left-2 sm:left-6 w-[200px] sm:w-[230px] bg-[#0e0e1c]/95 border border-orange-500/25 rounded-2xl p-3.5 shadow-2xl shadow-black/40 anim-float"
      >
        <div className="text-[10px] uppercase tracking-wider text-orange-400/80 font-semibold mb-1.5 flex items-center gap-1.5">
          <Globe className="w-3 h-3" /> Twoja strona
        </div>
        <div className="aspect-[4/3] rounded-lg bg-gradient-to-br from-orange-500/30 to-amber-500/10 mb-2 flex items-center justify-center">
          <Dog className="w-9 h-9 text-orange-300/80" />
        </div>
        <div className="h-1.5 bg-white/10 rounded-full mb-1.5"><div className="h-full w-3/4 bg-orange-400 rounded-full" /></div>
        <div className="h-1 bg-white/5 rounded-full w-1/2" />
      </motion.div>

      {/* Card: Katalog APISAI */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
        className="absolute top-12 right-2 sm:right-6 w-[210px] sm:w-[240px] bg-[#0e0e1c]/95 border border-violet-500/25 rounded-2xl p-3.5 shadow-2xl shadow-black/40 anim-float-delay"
      >
        <div className="text-[10px] uppercase tracking-wider text-violet-300/90 font-semibold mb-2 flex items-center gap-1.5">
          <Search className="w-3 h-3" /> Katalog APISAI
        </div>
        <div className="flex items-center gap-2 bg-white/[0.04] rounded-lg px-2 py-1.5 mb-2 border border-white/[0.06]">
          <Search className="w-3 h-3 text-zinc-500" />
          <span className="text-[11px] text-zinc-400">labrador · Warszawa</span>
        </div>
        <div className="space-y-1.5">
          {[1, 2].map((i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-gradient-to-br from-violet-500/40 to-pink-500/30" />
              <div className="flex-1">
                <div className="h-1.5 bg-white/15 rounded w-3/4" />
                <div className="h-1 bg-white/5 rounded w-1/2 mt-1" />
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Card: Post / Promocja */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
        className="absolute bottom-16 left-6 sm:left-16 w-[200px] sm:w-[220px] bg-[#0e0e1c]/95 border border-pink-500/25 rounded-2xl p-3.5 shadow-2xl shadow-black/40 anim-float"
      >
        <div className="text-[10px] uppercase tracking-wider text-pink-300/90 font-semibold mb-2 flex items-center gap-1.5">
          <Megaphone className="w-3 h-3" /> Post / Promocja
        </div>
        <div className="text-[11px] text-zinc-300 leading-snug mb-2">
          „Nowy miot labradorów dostępny — sprawdź szczeniaki w hodowli."
        </div>
        <div className="flex items-center gap-2 text-[9px] text-zinc-500">
          <span className="px-2 py-0.5 bg-pink-500/10 text-pink-300 rounded-full">FB</span>
          <span className="px-2 py-0.5 bg-pink-500/10 text-pink-300 rounded-full">IG</span>
          <span className="px-2 py-0.5 bg-pink-500/10 text-pink-300 rounded-full">Katalog</span>
        </div>
      </motion.div>

      {/* Card: Wiadomości */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75 }}
        className="absolute bottom-4 right-6 sm:right-12 w-[210px] sm:w-[230px] bg-[#0e0e1c]/95 border border-emerald-500/25 rounded-2xl p-3.5 shadow-2xl shadow-black/40 anim-float-delay"
      >
        <div className="text-[10px] uppercase tracking-wider text-emerald-300/90 font-semibold mb-2 flex items-center gap-1.5">
          <MessageSquare className="w-3 h-3" /> Wiadomości
        </div>
        <div className="space-y-1.5">
          <div className="bg-white/[0.04] rounded-lg p-2 border border-white/[0.06]">
            <div className="text-[10px] text-emerald-300 font-medium mb-0.5">Anna K. · 2 min temu</div>
            <div className="text-[11px] text-zinc-300">Pytam o dostępność miotu...</div>
          </div>
          <div className="bg-white/[0.04] rounded-lg p-2 border border-white/[0.06]">
            <div className="text-[10px] text-emerald-300 font-medium mb-0.5">Marek W. · 8 min temu</div>
            <div className="text-[11px] text-zinc-300">Chciałbym zarezerwować...</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/* ─── SECTION: HERO ─── */
function HeroSection() {
  return (
    <section className="relative pt-28 pb-16 sm:pt-32 sm:pb-24" data-testid="hero-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/25 text-orange-300 text-xs font-medium mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Hodowle · biznesy · usługi lokalne
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight" data-testid="hero-headline">
              Więcej widoczności.
              <br />
              <span className="bg-gradient-to-r from-orange-400 via-orange-300 to-amber-300 bg-clip-text text-transparent">
                Więcej klientów.
              </span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
              className="mt-6 text-base sm:text-lg text-zinc-400 leading-relaxed max-w-xl">
              APISAI tworzy stronę, katalog i promocję dla hodowli oraz lokalnych biznesów.{' '}
              <span className="text-white font-semibold">Odpowiedz na kilka pytań i zobacz gotowy podgląd — bez logowania.</span>{' '}
              Publikujesz dopiero wtedy, kiedy chcesz.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
              className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/stworz"
                className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-orange-500 hover:bg-orange-400 text-white rounded-xl font-semibold shadow-2xl shadow-orange-500/30 transition anim-pulse-ring"
                data-testid="hero-cta-primary">
                Wygeneruj swoją stronę za darmo <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <a href="#jak-dziala"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-white/15 hover:border-orange-500/40 hover:bg-orange-500/5 transition text-zinc-200 font-semibold"
                data-testid="hero-cta-secondary">
                Zobacz jak działa
              </a>
              <Link to="/demo"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-white/15 hover:border-orange-500/40 hover:bg-orange-500/5 transition text-zinc-200 font-semibold"
                data-testid="hero-cta-demo">
                Wypróbuj demo
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
              className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-zinc-300">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Bez logowania na start</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Gotowy podgląd w kilka kroków</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Strona, katalog i zapytania w jednym miejscu</span>
            </motion.div>
          </div>
          <div className="relative">
            <HeroEcosystemVisual />
          </div>
        </div>

        {/* Visibility chips strip */}
        <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="mt-12 sm:mt-16 flex flex-wrap items-center justify-center gap-2.5 sm:gap-4 text-xs text-zinc-500">
          <span className="text-zinc-600 uppercase tracking-wider text-[10px]">Widoczność tam, gdzie szukają klienci:</span>
          {['Google', 'Mapy Google', 'Bing', 'Facebook', 'Instagram', 'Katalog APISAI', 'WhatsApp', 'AI Search'].map((s) => (
            <span key={s} className="px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-zinc-400">{s}</span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ─── SECTION: VISIBILITY ECOSYSTEM ─── */
function VisibilityEcosystem() {
  const nodes = [
    { label: 'Google', icon: Globe, color: 'text-blue-300', bg: 'from-blue-500/20 to-blue-700/10' },
    { label: 'Bing', icon: Search, color: 'text-cyan-300', bg: 'from-cyan-500/20 to-cyan-700/10' },
    { label: 'Mapy Google', icon: MapPin, color: 'text-emerald-300', bg: 'from-emerald-500/20 to-emerald-700/10' },
    { label: 'Facebook', icon: MessageCircle, color: 'text-indigo-300', bg: 'from-indigo-500/20 to-indigo-700/10' },
    { label: 'Instagram', icon: Eye, color: 'text-pink-300', bg: 'from-pink-500/20 to-pink-700/10' },
    { label: 'Katalog APISAI', icon: Search, color: 'text-orange-300', bg: 'from-orange-500/25 to-amber-500/10' },
    { label: 'WhatsApp', icon: MessageSquare, color: 'text-emerald-300', bg: 'from-emerald-500/20 to-green-700/10' },
    { label: 'AI Search', icon: Sparkles, color: 'text-violet-300', bg: 'from-violet-500/20 to-purple-700/10' },
  ];
  return (
    <section className="py-20 sm:py-28 relative" data-testid="visibility-ecosystem">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/25 text-violet-300 text-xs font-medium mb-4">
            <Eye className="w-3.5 h-3.5" /> Ekosystem widoczności
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="visibility-headline">
            Klienci mogą znaleźć Cię w wielu miejscach
          </h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            APISAI pomaga budować widoczność tam, gdzie klienci szukają usług, hodowli i lokalnych firm.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          {nodes.map((n, i) => (
            <motion.div key={n.label}
              initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
              className={`relative bg-gradient-to-br ${n.bg} border border-white/10 rounded-2xl p-5 hover:border-white/20 transition-colors`}>
              <div className="w-10 h-10 rounded-xl bg-black/30 flex items-center justify-center mb-3">
                <n.icon className={`w-5 h-5 ${n.color}`} />
              </div>
              <div className="text-sm font-semibold text-white">{n.label}</div>
              <div className="text-[11px] text-zinc-500 mt-1">{n.label === 'Katalog APISAI' ? 'discovery layer' : 'wyszukiwarka / kanał'}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── SECTION: KATALOG SHOWCASE ─── */
function CatalogShowcase() {
  const cards = [
    { kind: 'Hodowla', name: 'Wolfe vom Katzenberg', loc: 'Warszawa', tag: 'Owczarki niemieckie', Icon: Dog },
    { kind: 'Groomer', name: 'Pies Pomarańczowy', loc: 'Kraków', tag: 'Strzyżenie psów', Icon: Scissors },
    { kind: 'Restauracja', name: 'Łowicka 25', loc: 'Warszawa', tag: 'Dog-friendly · brunch', Icon: UtensilsCrossed },
  ];
  return (
    <section className="py-20 sm:py-28 relative" data-testid="catalog-showcase">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/25 text-orange-300 text-xs font-medium mb-4">
              <Search className="w-3.5 h-3.5" /> Katalog APISAI
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Bądź widoczna tam, gdzie klient szuka hodowli lub usługi
            </h2>
            <p className="mt-4 text-zinc-400 leading-relaxed">
              Twój profil może pojawić się w katalogu APISAI razem ze zdjęciami, opisem, lokalizacją i formularzem zapytania.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link to="/hodowle" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-orange-500 hover:bg-orange-400 text-white font-semibold transition" data-testid="catalog-cta-hodowle">
                Zobacz katalog hodowli <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/firmy" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-white/15 hover:border-orange-500/40 text-zinc-200 font-semibold transition" data-testid="catalog-cta-biznesy">
                Zobacz katalog biznesów
              </Link>
            </div>
          </div>
          <div className="lg:col-span-3">
            <div className="bg-[#0c0c1a] border border-white/10 rounded-2xl p-5 sm:p-6 shadow-2xl shadow-black/40">
              {/* mock search bar */}
              <div className="flex items-center gap-3 bg-white/[0.04] rounded-xl px-4 py-3 border border-white/[0.08]">
                <Search className="w-4 h-4 text-zinc-500" />
                <input
                  defaultValue="szczenięta labrador Warszawa"
                  readOnly
                  className="bg-transparent flex-1 text-sm text-zinc-200 outline-none"
                />
                <kbd className="text-[10px] text-zinc-500 px-2 py-0.5 bg-white/5 rounded">Szukaj</kbd>
              </div>
              {/* filters */}
              <div className="mt-3 flex flex-wrap gap-2">
                {['Hodowle', 'Szczenięta', 'Owczarki niemieckie', 'Warszawa', 'Dostępne'].map((f) => (
                  <span key={f} className="text-[11px] px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/[0.08] text-zinc-400">{f}</span>
                ))}
              </div>
              {/* cards */}
              <div className="mt-5 grid sm:grid-cols-3 gap-3">
                {cards.map((c) => (
                  <div key={c.name} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3.5 hover:border-orange-500/30 transition">
                    <div className="aspect-[4/3] rounded-lg bg-gradient-to-br from-orange-500/20 to-violet-500/10 mb-3 flex items-center justify-center">
                      <c.Icon className="w-8 h-8 text-orange-200/80" strokeWidth={1.5} />
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-orange-300/80 font-semibold">{c.kind}</div>
                    <div className="text-sm font-semibold text-white truncate mt-0.5">{c.name}</div>
                    <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.loc}</div>
                    <div className="text-[10px] text-zinc-500 mt-1.5">{c.tag}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── SECTION: BUSINESS SLIDER ─── */
function BusinessSlider() {
  const cards = [
    { kind: 'Hodowle', Icon: Dog, color: 'from-orange-500/25 to-amber-500/10', text: 'Pokaż psy, mioty i reproduktory. Zdobądź zapytania od przyszłych właścicieli.' },
    { kind: 'Mioty i reproduktory', Icon: PawPrint, color: 'from-amber-500/25 to-orange-500/10', text: 'Ogłaszaj dostępne mioty i prezentuj reproduktory do krycia.' },
    { kind: 'Groomerzy', Icon: Scissors, color: 'from-pink-500/25 to-rose-500/10', text: 'Prezentuj realizacje i zdobywaj klientów lokalnie.' },
    { kind: 'Hotele dla psów', Icon: Hotel, color: 'from-blue-500/25 to-indigo-500/10', text: 'Pokazuj pokoje, opiekunów i opinie. Klienci łatwiej Cię znajdą.' },
    { kind: 'Szkoleniowcy', Icon: GraduationCap, color: 'from-emerald-500/25 to-teal-500/10', text: 'Pokaż metody, kursy i efekty. Zapełnij grafik zajęć.' },
    { kind: 'Handlerzy', Icon: Award, color: 'from-violet-500/25 to-purple-500/10', text: 'Prezentuj sukcesy wystawowe i buduj renomę w środowisku.' },
    { kind: 'Lokalne firmy usługowe', Icon: Store, color: 'from-cyan-500/25 to-sky-500/10', text: 'Weterynarze, sklepy zoologiczne, behawioryści i inne usługi.' },
    { kind: 'Restauracje, bary, fryzjerzy', Icon: UtensilsCrossed, color: 'from-slate-500/25 to-zinc-500/10', text: 'System działa też dla innych lokalnych biznesów — to dalsze przykłady.' },
  ];
  return (
    <section className="py-20 sm:py-28 relative" data-testid="business-slider">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Stworzone dla hodowli — działa też dla lokalnych biznesów</h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Najpierw hodowle, mioty i reproduktory. Ten sam system pomaga też groomerom, hotelom dla psów, szkoleniowcom i innym lokalnym usługom.
          </p>
        </div>
        <div className="flex sm:grid sm:grid-cols-3 gap-4 sm:gap-5 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
          {cards.map((c, i) => (
            <motion.div key={c.kind}
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className={`flex-shrink-0 w-[260px] sm:w-auto bg-gradient-to-br ${c.color} border border-white/10 rounded-2xl p-5 sm:p-6 hover:border-white/25 transition-colors`}
              data-testid={`business-card-${c.kind.toLowerCase()}`}>
              <div className="w-12 h-12 rounded-xl bg-black/30 flex items-center justify-center mb-4">
                <c.Icon className="w-6 h-6 text-white/85" />
              </div>
              <div className="text-lg font-bold text-white mb-1.5">{c.kind}</div>
              <div className="text-sm text-zinc-300/90 leading-relaxed">{c.text}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── SECTION: WOLFE CASE STUDY ─── */
function WolfeCaseStudy() {
  return (
    <section className="py-20 sm:py-28 relative bg-gradient-to-b from-transparent via-[#0a0a16] to-transparent" data-testid="wolfe-case-study">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 text-xs font-medium mb-4">
            <ShieldCheck className="w-3.5 h-3.5" /> Realny case study
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
            Zobacz jak APISAI pomaga hodowli wyglądać profesjonalnie online
          </h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Wolfe vom Katzenberg to przykład hodowli, która wykorzystuje nowoczesną stronę, widoczność online i katalog APISAI do budowania zaufania oraz zapytań.
          </p>
        </div>

        {/* Flow: Problem → Strona → Katalog → Zapytania */}
        <div className="grid sm:grid-cols-4 gap-3 sm:gap-4 mb-10">
          {[
            { t: 'Problem', d: 'Hodowla bez nowoczesnej obecności online.', color: 'text-red-300', bg: 'bg-red-500/10', border: 'border-red-500/25' },
            { t: 'Rozwiązanie', d: 'Profesjonalna strona z planami hodowlanymi.', color: 'text-orange-300', bg: 'bg-orange-500/10', border: 'border-orange-500/25' },
            { t: 'Widoczność', d: 'Katalog APISAI + Google + Bing.', color: 'text-violet-300', bg: 'bg-violet-500/10', border: 'border-violet-500/25' },
            { t: 'Zapytania', d: 'Bezpośrednie wiadomości od klientów.', color: 'text-emerald-300', bg: 'bg-emerald-500/10', border: 'border-emerald-500/25' },
          ].map((s, i) => (
            <div key={s.t} className={`rounded-2xl p-4 ${s.bg} border ${s.border}`}>
              <div className={`text-[10px] uppercase tracking-wider font-semibold ${s.color} mb-1`}>Krok {i + 1}</div>
              <div className="text-base font-bold text-white">{s.t}</div>
              <div className="text-xs text-zinc-400 mt-1.5 leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>

        {/* Real screenshots */}
        <div className="grid lg:grid-cols-2 gap-5 sm:gap-7">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="group relative rounded-2xl overflow-hidden border border-white/10 bg-[#0c0c1a] shadow-2xl shadow-black/40">
            <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-[10px] font-medium text-emerald-300 border border-emerald-500/30">
              Realny screen · Homepage
            </div>
            <img src="/landing-spec/wolfe/wolfe_home_real_screenshot.png" alt="Wolfe vom Katzenberg – strona główna"
              className="w-full h-auto block group-hover:scale-[1.01] transition-transform duration-500" loading="lazy"
              data-testid="wolfe-screen-home" />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
            className="group relative rounded-2xl overflow-hidden border border-white/10 bg-[#0c0c1a] shadow-2xl shadow-black/40">
            <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-[10px] font-medium text-violet-300 border border-violet-500/30">
              Realny screen · Plany hodowlane
            </div>
            <img src="/landing-spec/wolfe/wolfe_plany_hodowlane_real_screenshot.png" alt="Wolfe vom Katzenberg – plany hodowlane"
              className="w-full h-auto block group-hover:scale-[1.01] transition-transform duration-500" loading="lazy"
              data-testid="wolfe-screen-plans" />
          </motion.div>
        </div>

        <div className="mt-10 text-center">
          <Link to="/historie-sukcesu/wolfe-vom-katzenberg"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/[0.05] hover:bg-white/[0.08] border border-white/15 hover:border-orange-500/40 text-zinc-200 font-semibold transition"
            data-testid="wolfe-case-cta">
            Zobacz pełną historię <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─── SECTION: HOW IT WORKS ─── */
function HowItWorks() {
  const steps = [
    { t: 'Stwórz profil', d: 'W kilka minut. Hodowla, biznes lub usługa.', Icon: Sparkles },
    { t: 'Pokaż ofertę', d: 'Psy, mioty, realizacje, menu, usługi.', Icon: Eye },
    { t: 'Bądź widoczny', d: 'Katalog APISAI, Google, mapy, social media.', Icon: Globe },
    { t: 'Zdobywaj zapytania', d: 'Wiadomości i leady w jednym miejscu.', Icon: MessageSquare },
  ];
  return (
    <section id="jak-dziala" className="py-20 sm:py-28 relative scroll-mt-24" data-testid="how-it-works">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Jak działa APISAI</h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Cztery proste kroki od profilu do pierwszych zapytań.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {steps.map((s, i) => (
            <motion.div key={s.t}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="relative bg-[#0c0c1a] border border-white/10 rounded-2xl p-5 hover:border-orange-500/30 transition">
              <div className="text-[10px] uppercase tracking-wider text-orange-300/80 font-bold mb-3">Krok {i + 1}</div>
              <div className="w-11 h-11 rounded-xl bg-orange-500/10 border border-orange-500/25 flex items-center justify-center mb-4">
                <s.Icon className="w-5 h-5 text-orange-300" />
              </div>
              <div className="text-lg font-bold text-white">{s.t}</div>
              <div className="text-sm text-zinc-400 mt-1.5 leading-relaxed">{s.d}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── SECTION: LIVE ECOSYSTEM ─── */
function LiveEcosystemSection() {
  return (
    <section className="py-16 sm:py-20 relative" data-testid="live-ecosystem-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="bg-gradient-to-br from-violet-950/40 via-[#0a0a16] to-[#0a0a16] border border-white/10 rounded-3xl p-6 sm:p-10 relative overflow-hidden">
          <div className="grain-overlay absolute inset-0 opacity-30 pointer-events-none" />
          <div className="grid md:grid-cols-2 gap-8 items-center relative">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 text-xs font-medium mb-4">
                <Sparkles className="w-3.5 h-3.5" /> Ekosystem APISAI
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Hodowle i biznesy są tu aktywne</h2>
              <p className="mt-3 text-zinc-400 leading-relaxed text-sm sm:text-base">
                Każdy obejrzany profil, każde kliknięcie WhatsApp i każda nowa wiadomość to realny ruch wewnątrz APISAI. Twój profil dołączy do tego samego systemu.
              </p>
            </div>
            <div>
              <PublicLiveTicker limit={6} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── SECTION: FINAL CTA ─── */
function FinalCTA() {
  return (
    <section className="py-20 sm:py-28 relative" data-testid="final-cta">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <div className="relative inline-block">
          <div className="absolute -inset-6 bg-orange-500/15 blur-3xl rounded-full" />
          <h2 className="relative text-3xl sm:text-5xl font-bold tracking-tight">
            Pokaż swoją hodowlę lub biznes.<br />
            <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">Zdobądź więcej zapytań.</span>
          </h2>
        </div>
        <p className="mt-5 text-zinc-400 leading-relaxed max-w-xl mx-auto">
          Stwórz stronę za darmo, zobacz podgląd i zdecyduj później, czy chcesz ją opublikować.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/stworz"
            className="group inline-flex items-center justify-center gap-2 px-7 py-4 bg-orange-500 hover:bg-orange-400 text-white rounded-xl font-semibold shadow-2xl shadow-orange-500/30 transition"
            data-testid="final-cta-primary">
            Wygeneruj swoją stronę za darmo <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <Link to="/cennik"
            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl border border-white/15 hover:border-orange-500/40 text-zinc-200 font-semibold transition">
            Zobacz cennik
          </Link>
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-zinc-500">
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Bez karty</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Strona w 5 minut</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Widoczność w katalogu</span>
        </div>
      </div>
    </section>
  );
}

/* ─── SCROLL TO TOP ─── */
function ScrollToTopBtn() {
  const [v, setV] = useState(false);
  useEffect(() => {
    const fn = () => setV(window.pageYOffset > 600);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);
  return (
    <AnimatePresence>
      {v && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-20 sm:bottom-6 right-5 z-40 p-3 rounded-full bg-orange-500 text-white shadow-lg shadow-orange-500/40 hover:scale-110 transition-transform"
          data-testid="scroll-to-top">
          <ArrowUp className="w-5 h-5" />
        </motion.button>
      )}
    </AnimatePresence>
  );
}

/* ─── MOBILE STICKY CTA ─── */
function MobileStickyCTA() {
  const [v, setV] = useState(false);
  useEffect(() => {
    const fn = () => setV(window.pageYOffset > 500);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);
  return (
    <AnimatePresence>
      {v && (
        <motion.div
          initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }}
          className="lg:hidden fixed bottom-3 left-3 right-3 z-40">
          <Link to="/stworz"
            className="flex items-center justify-center gap-2 w-full py-3.5 bg-orange-500 text-white rounded-xl font-semibold shadow-2xl shadow-orange-500/40"
            data-testid="mobile-sticky-cta">
            Wygeneruj stronę za darmo <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ─── SITE FOOTER (premium, bez emoji) ─── */
const FOOTER_BREEDS = [
  { name: 'Owczarek niemiecki', slug: 'owczarek-niemiecki' },
  { name: 'Labrador retriever', slug: 'labrador-retriever' },
  { name: 'Golden retriever', slug: 'golden-retriever' },
  { name: 'Border collie', slug: 'border-collie' },
  { name: 'Yorkshire terrier', slug: 'yorkshire-terrier' },
  { name: 'Maltańczyk', slug: 'maltanczyk' },
  { name: 'Husky syberyjski', slug: 'husky-syberyjski' },
  { name: 'Cane corso', slug: 'cane-corso' },
];

function SiteFooter() {
  return (
    <footer className="relative border-t border-white/[0.08] bg-[#05050c]/70 mt-16" data-testid="landing-footer">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid gap-10 md:grid-cols-5">
          {/* Brand */}
          <div className="md:col-span-2">
            <Link to="/" className="inline-flex items-center gap-2" data-testid="footer-brand-link">
              <img src="/landing-spec/brand/apisai_logo_orange_hex.png" alt="APISAI" className="h-7 w-7 rounded-lg" />
              <span className="text-lg font-bold text-white">APISAI</span>
            </Link>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed max-w-xs">
              System widoczności i pozyskiwania klientów online dla hodowli oraz lokalnych biznesów — strona, katalog, promocja i zapytania w jednym miejscu.
            </p>
            <Link
              to="/stworz"
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-400 text-white text-sm font-semibold transition"
              data-testid="footer-cta-stworz"
            >
              Wygeneruj stronę za darmo <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Dla hodowców */}
          <div data-testid="footer-col-hodowcy">
            <p className="text-xs font-semibold uppercase tracking-wider text-white/50 mb-4 inline-flex items-center gap-2">
              <PawPrint className="w-4 h-4 text-orange-400" /> Dla hodowców
            </p>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/katalog?type=hodowle" className="text-zinc-400 hover:text-white transition-colors">Katalog hodowli</Link></li>
              <li><Link to="/katalog?type=mioty" className="text-zinc-400 hover:text-white transition-colors">Szczenięta i mioty</Link></li>
              <li><Link to="/katalog?type=reproduktory" className="text-zinc-400 hover:text-white transition-colors">Reproduktory</Link></li>
              <li><Link to="/rasy" className="text-zinc-400 hover:text-white transition-colors">Rasy psów</Link></li>
              <li><Link to="/strona-dla-hodowli" className="text-zinc-400 hover:text-white transition-colors">Strona dla hodowli</Link></li>
            </ul>
          </div>

          {/* Dla biznesu */}
          <div data-testid="footer-col-biznes">
            <p className="text-xs font-semibold uppercase tracking-wider text-white/50 mb-4 inline-flex items-center gap-2">
              <Store className="w-4 h-4 text-orange-400" /> Dla biznesu
            </p>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/katalog/uslugi" className="text-zinc-400 hover:text-white transition-colors">Katalog usług</Link></li>
              <li><Link to="/strona-dla-firmy" className="text-zinc-400 hover:text-white transition-colors">Strona dla firmy</Link></li>
              <li><Link to="/dla-firm" className="text-zinc-400 hover:text-white transition-colors">Dla firm i usług</Link></li>
              <li><Link to="/cennik" className="text-zinc-400 hover:text-white transition-colors">Cennik</Link></li>
            </ul>
          </div>

          {/* Informacje */}
          <div data-testid="footer-col-info">
            <p className="text-xs font-semibold uppercase tracking-wider text-white/50 mb-4 inline-flex items-center gap-2">
              <Globe className="w-4 h-4 text-orange-400" /> Informacje
            </p>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/blog" className="text-zinc-400 hover:text-white transition-colors">Baza wiedzy</Link></li>
              <li><Link to="/kontakt" className="text-zinc-400 hover:text-white transition-colors">Kontakt</Link></li>
              <li><Link to="/regulamin" className="text-zinc-400 hover:text-white transition-colors">Regulamin</Link></li>
              <li><Link to="/polityka-prywatnosci" className="text-zinc-400 hover:text-white transition-colors">Polityka prywatności</Link></li>
            </ul>
          </div>
        </div>

        {/* Popularne rasy — wewnętrzne linkowanie SEO */}
        <div className="mt-12 pt-8 border-t border-white/[0.06]">
          <p className="text-xs font-semibold uppercase tracking-wider text-white/40 mb-3">Popularne rasy</p>
          <div className="flex flex-wrap gap-2" data-testid="footer-popular-breeds">
            {FOOTER_BREEDS.map((b) => (
              <Link
                key={b.slug}
                to={`/rasy/${b.slug}`}
                className="text-xs px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-zinc-400 hover:text-white hover:border-orange-400/40 transition"
                data-testid={`footer-breed-${b.slug}`}
              >
                {b.name}
              </Link>
            ))}
            <Link to="/rasy" className="text-xs px-3 py-1.5 rounded-full text-orange-300 hover:text-orange-200 inline-flex items-center gap-1 transition">
              Wszystkie rasy <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="mt-10 pt-6 border-t border-white/[0.06] flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-zinc-500">
          <span>© {new Date().getFullYear()} APISAI · System widoczności i pozyskiwania klientów online.</span>
          <div className="flex items-center gap-5">
            <Link to="/regulamin" className="hover:text-white transition-colors">Regulamin</Link>
            <Link to="/polityka-prywatnosci" className="hover:text-white transition-colors">Prywatność</Link>
            <Link to="/kontakt" className="hover:text-white transition-colors">Kontakt</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ═══════════════════════════════════════════
   MAIN
═══════════════════════════════════════════ */
export default function LandingNew() {
  return (
    <div className="min-h-screen bg-[#070710] text-white overflow-hidden" data-testid="landing-page">
      <Helmet>
        <title>APISAI — Strona, katalog i promocja dla hodowli i biznesów</title>
        <meta name="description" content="APISAI łączy profesjonalną stronę, katalog, promocję i wiadomości w jednym miejscu. Klienci łatwiej znajdują Twoją hodowlę lub biznes, a Ty szybciej dostajesz zapytania." />
        <meta property="og:title" content="APISAI — Więcej widoczności. Więcej klientów." />
        <meta property="og:description" content="Strona, katalog i promocja w jednym miejscu. Dla hodowli i lokalnych biznesów." />
        <link rel="canonical" href="https://apisai.pl/" />
      </Helmet>
      <style>{STYLES}</style>

      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-950/20 via-[#070710] to-[#070710]" />
      </div>

      <PublicNav />
      <ScrollToTopBtn />
      <MobileStickyCTA />

      <main className="relative">
        <HeroSection />
        <VisibilityEcosystem />
        <CatalogShowcase />
        <BusinessSlider />
        <HowItWorks />
        <LiveEcosystemSection />
        <FinalCTA />
      </main>

      <SiteFooter />
    </div>
  );
}
